"use client"

import React, { useState, useMemo, useEffect } from "react"
import type { ColumnDefinition } from "../../types/project"
import { useTheme } from "../../contexts/ThemeContext"
import { UnitModal } from "./UnitModal"
import { ColumnVisibilityModal } from "./ColumnVisibilityModal"
import convert from "convert-units"
import type { Unit } from "../../types/table"
import { IconButton, Text } from "react-native-paper"
import Cookies from 'js-cookie'


interface NestedRowTableBaseProps<T> {
  data: T[]
  columns: ColumnDefinition[]
  levelKey: string
  childrenKey: string
  tableName: string
  defaultVisibleColumns?: string[]
  title?: string
  OnExpandedButtonClick?: () => void
  menuAction?: {
    type: "columnOptions" | "expandTable"
    visible: boolean
  }
}

export function NestedRowTableBase<T extends Record<string, any>>({
  data,
  columns,
  levelKey,
  childrenKey,
  tableName,
  defaultVisibleColumns,
  title,
  OnExpandedButtonClick,
  menuAction,
}: NestedRowTableBaseProps<T>) {
  const { theme } = useTheme()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: "ascending" | "descending"
  } | null>(null)
  const [unitModalVisible, setUnitModalVisible] = useState(false)
  const [activeColumn, setActiveColumn] = useState<string | null>(null)
  const [columnUnits, setColumnUnits] = useState<Record<string, Unit>>({})
  const [orderedColumns, setOrderedColumns] = useState<ColumnDefinition[]>(() => {
    // Try to load saved column order from cookies
    if (tableName) {
      const cookieKey = `table_${tableName}_columns`
      const storedState = Cookies.get(cookieKey)
      if (storedState) {
        try {
          const savedState = JSON.parse(storedState)
          if (savedState.orderedColumns) {
            // Map the saved column keys back to their full column definitions
            const savedColumns = savedState.orderedColumns
              .map(key => columns.find(col => col.key === key))
              .filter((col): col is ColumnDefinition => col !== undefined)

            // Add any new columns that weren't in the saved state
            const savedKeys = new Set(savedState.orderedColumns)
            const newColumns = columns.filter(col => !savedKeys.has(col.key))
            return [...savedColumns, ...newColumns]
          }
        } catch (error) {
          console.error("Error parsing saved column order:", error)
        }
      }
    }
    return columns
  })
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    // Try to load saved column state from cookies
    if (tableName) {
      const cookieKey = `table_${tableName}_columns`
      const storedState = Cookies.get(cookieKey)
      if (storedState) {
        try {
          const savedState = JSON.parse(storedState)
          const savedColumns = savedState.visibleColumns.filter((col: string) =>
            columns.some(c => c.key === col)
          )
          if (savedColumns.length > 0) {
            return savedColumns
          }
        } catch (error) {
          console.error("Error parsing saved column state:", error)
        }
      }
    }
    return defaultVisibleColumns || columns.map(col => col.key)
  })
  const [columnVisibilityModalVisible, setColumnVisibilityModalVisible] = useState(false)
  const [isTableExpanded, setIsTableExpanded] = useState(false)

  // Handle menu actions
  useEffect(() => {
    if (menuAction?.visible) {
      if (menuAction.type === "columnOptions") {
        setColumnVisibilityModalVisible(true)
      } else if (menuAction.type === "expandTable") {
        setIsTableExpanded(true)
        OnExpandedButtonClick?.()
      }
    }
  }, [menuAction, OnExpandedButtonClick])

  const toggleRow = (id: string) => {
    const newExpandedRows = new Set(expandedRows)
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id)
    } else {
      newExpandedRows.add(id)
    }
    setExpandedRows(newExpandedRows)
  }

  const toggleTableExpand = () => {
    setIsTableExpanded(!isTableExpanded)
  }

  const sortedData = useMemo(() => {
    if (!sortConfig) return data

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      if (aValue === bValue) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      const comparison = aValue < bValue ? -1 : 1
      return sortConfig.direction === "ascending" ? comparison : -comparison
    })

    return sorted
  }, [data, sortConfig])

  const convertValueWithUnit = (value: number, fromUnit: Unit, toUnit: Unit): number => {
    try {
      return convert(value).from(fromUnit).to(toUnit)
    } catch (error) {
      console.warn("Error converting value, using original:", error)
      return value
    }
  }

  const formatValue = (value: any, column: ColumnDefinition) => {
    if (value === null || value === undefined) return ""

    if (typeof value === 'number') {
      let displayValue = value
      const columnUnit = columnUnits[column.key]
      if (columnUnit && column.units) {
        displayValue = convertValueWithUnit(value, column.units as Unit, columnUnit)
      }
      const formattedNumber = Number(displayValue).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return columnUnit ? `${formattedNumber} ${columnUnit}` : formattedNumber
    }

    return value.toString()
  }

  const handleUnitClick = (columnKey: string) => {
    const column = columns.find(col => col.key === columnKey)
    if (column?.units) {
      setActiveColumn(columnKey)
      setUnitModalVisible(true)
    }
  }

  const handleUnitSelect = (unit: Unit) => {
    if (activeColumn) {
      setColumnUnits(prev => ({
        ...prev,
        [activeColumn]: unit
      }))
    }
    setUnitModalVisible(false)
    setActiveColumn(null)
  }

  const handleToggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => {
      const newColumns = prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]

      // Save to cookie
      if (tableName) {
        const cookieKey = `table_${tableName}_columns`
        const state = {
          visibleColumns: newColumns,
          orderedColumns: orderedColumns.map(col => col.key)
        }
        Cookies.set(cookieKey, JSON.stringify(state), { expires: 365 })
      }

      return newColumns
    })
  }

  const handleReorderColumns = (newOrder: string[]) => {
    const reorderedColumns = newOrder
      .map(key => columns.find(col => col.key === key))
      .filter((col): col is ColumnDefinition => col !== undefined)

    setOrderedColumns(reorderedColumns)

    // Save to cookie
    if (tableName) {
      const cookieKey = `table_${tableName}_columns`
      const state = {
        visibleColumns,
        orderedColumns: newOrder
      }
      Cookies.set(cookieKey, JSON.stringify(state), { expires: 365 })
    }
  }

  const renderRow = (item: T, level = 0, index: number) => {
    const rowId = item[levelKey]
    const hasChildren = item[childrenKey]?.length > 0
    const isExpanded = expandedRows.has(rowId)

    return (
      <React.Fragment key={rowId}>
        <tr style={{ backgroundColor: index % 2 === 1 ? theme.colors.elevation.level1 : theme.colors.background }}>
          {orderedColumns
            .filter(column => visibleColumns.includes(column.key))
            .map((column, index) => (
              <td
                key={column.key}
                style={{
                  padding: "4px 6px",
                  textAlign: column.align || "left",
                  borderBottom: `1px solid ${theme.colors.outline}`,
                  whiteSpace: "normal",
                  color: theme.colors.onSurface,
                  fontSize: "12px",
                  cursor: column.units ? "pointer" : "default",
                  width: column.key === columns[0].key ? "160px" : "100px",
                  minWidth: column.key === columns[0].key ? "160px" : "100px",
                }}
                onClick={() => column.units && handleUnitClick(column.key)}
              >
                {index === 0 && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: `${level * 20}px`,
                    }}
                  >
                    {hasChildren && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleRow(rowId)
                        }}
                        style={{
                          marginRight: "8px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          color: theme.colors.primary,
                        }}
                      >
                        {isExpanded ? "−" : "+"}
                      </button>
                    )}
                    {formatValue(item[column.key], column)}
                  </span>
                )}
                {index !== 0 && formatValue(item[column.key], column)}
              </td>
            ))}
        </tr>
        {hasChildren && isExpanded && item[childrenKey].map((child: T, childIndex: number) => renderRow(child, level + 1, index + childIndex + 1))}
      </React.Fragment>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: OnExpandedButtonClick ? '100%' : '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 16px",
        backgroundColor: theme.colors.surface,
        borderBottom: `1px solid ${theme.colors.outline}`,
        flexShrink: 0  // Add this line
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}>
          {title && (
            <span
              style={{
                color: theme.colors.onSurface,
                fontSize: "16px",
                fontWeight: "500",
              }}
            >
              {title}
            </span>
          )}
          {OnExpandedButtonClick && (
            <IconButton
              icon="arrow-expand"
              size={20}
              onPress={OnExpandedButtonClick}
            />
          )}
          <IconButton
            icon="cog"
            size={20}
            onPress={() => setColumnVisibilityModalVisible(true)}
          />
        </div>
      </div> */}
      <div style={{
        position: 'relative',
        overflowY: 'auto',
        height: OnExpandedButtonClick ? '600px' : 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
        // minHeight: OnExpandedButtonClick ?  '600px':'calc(100vh - 56px)',
        padding: "0 2px 16px 2px", // Add padding 
      }}>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          border: `1px solid ${theme.colors.outline}`,
          backgroundColor: theme.colors.surface,
          tableLayout: "fixed",
          marginBottom: '16px', // Push content to top,       
        }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme.colors.elevation.level1
          }}>
            <tr style={{ backgroundColor: theme.colors.elevation.level1 }}>
              {orderedColumns
                .filter(column => visibleColumns.includes(column.key))
                .map((column) => (
                  <th
                    key={column.key}
                    style={{
                      padding: "4px 6px",
                      textAlign: column.align || "left",
                      borderBottom: `1px solid ${theme.colors.outline}`,
                      cursor: column.units ? "pointer" : "default",
                      whiteSpace: "normal",
                      color: theme.colors.onSurface,
                      fontSize: "12px",
                      fontWeight: "bold",
                      width: column.key === columns[0].key ? "160px" : "100px",
                      minWidth: column.key === columns[0].key ? "160px" : "100px",
                    }}
                    onClick={() => {
                      if (column.units) {
                        handleUnitClick(column.key)
                      } else if (column.sortable) {
                        setSortConfig({
                          key: column.field,
                          direction:
                            sortConfig?.key === column.field && sortConfig.direction === "ascending"
                              ? "descending"
                              : "ascending",
                        })
                      }
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>
                      {column.title}
                      {sortConfig?.key === column.field && (
                        <span style={{ color: theme.colors.primary, marginLeft: "4px" }}>
                          {sortConfig.direction === "ascending" ? "↑" : "↓"}
                        </span>
                      )}
                    </div>
                    {column.units && (
                      <div style={{
                        fontSize: "11px",
                        color: theme.colors.primary,
                        cursor: "pointer",
                      }}>
                        [{columnUnits[column.key] || column.units}]
                      </div>
                    )}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody style={{
            verticalAlign: 'center' // Align content to top
          }}>{sortedData.map((item, index) => renderRow(item, 0, index))}</tbody>
        </table>
      </div>
      <UnitModal
        visible={unitModalVisible}
        onDismiss={() => {
          setUnitModalVisible(false)
          setActiveColumn(null)
        }}
        currentUnit={activeColumn ? (columnUnits[activeColumn] || columns.find(col => col.key === activeColumn)?.units as Unit) : undefined}
        onUnitSelect={handleUnitSelect}
        measure={activeColumn ? convert().describe(columns.find(col => col.key === activeColumn)?.units as Unit).measure : undefined}
      />

      <ColumnVisibilityModal
        visible={columnVisibilityModalVisible}
        onDismiss={() => setColumnVisibilityModalVisible(false)}
        columns={orderedColumns}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onReorderColumns={handleReorderColumns}
        tableName={tableName}
      />
    </div>
  )
}
